import type { DTechFileConfig } from '../types/DTechFileConfig';

/**
 * Utility for calculating sub-second timestamps from D-Tech sample numbers
 * 
 * Handles the conversion of sample numbers to precise timestamps for F-Data and Turbine files:
 * - F-Data: 40 samples (0-39) over 10 seconds = 250ms intervals, ascending
 * - Turbine: 10 samples (9-0) over 10 seconds = 1000ms intervals, descending
 */
export class DTechSubSecondCalculator {
  
  /**
   * Calculate precise sub-second timestamp from base timestamp and sample number
   * @param baseTimestamp The base timestamp for this sample group (from Date & Time column)
   * @param sampleNumber The sample number from the Sample no. column
   * @param config DTech file configuration containing sub-second sampling parameters
   * @returns Calculated precise timestamp or null if calculation fails
   */
  static calculateSubSecondTimestamp(
    baseTimestamp: Date,
    sampleNumber: number,
    config: DTechFileConfig
  ): Date | null {
    // Validate inputs
    if (!baseTimestamp || isNaN(baseTimestamp.getTime())) {
      return null;
    }
    
    if (!config.subSecondSampling) {
      return baseTimestamp; // No sub-second processing, return original timestamp
    }
    
    if (isNaN(sampleNumber) || !config.samplesPerPeriod || !config.sampleIntervalMs) {
      return null;
    }
    
    // Validate sample number is within expected range
    const isValidSample = this.validateSampleNumber(sampleNumber, config);
    if (!isValidSample) {
      console.warn(`Invalid sample number ${sampleNumber} for ${config.dtechDataType} (expected range based on ${config.sampleDirection} direction)`);
      return null;
    }
    
    // Calculate offset based on sample direction
    const offsetMs = this.calculateOffset(sampleNumber, config);
    if (offsetMs === null) {
      return null;
    }
    
    // Create new timestamp with sub-second precision
    const preciseTimestamp = new Date(baseTimestamp.getTime() + offsetMs);
    
    return preciseTimestamp;
  }
  
  /**
   * Calculate the millisecond offset from base timestamp for a given sample number
   * @param sampleNumber The sample number to calculate offset for
   * @param config DTech file configuration
   * @returns Millisecond offset or null if calculation fails
   */
  private static calculateOffset(sampleNumber: number, config: DTechFileConfig): number | null {
    if (!config.sampleDirection || !config.sampleIntervalMs || !config.samplesPerPeriod) {
      return null;
    }
    
    let offset: number;
    
    switch (config.sampleDirection) {
      case 'ascending':
        // F-Data: sample 0 = +0ms, sample 1 = +250ms, sample 39 = +9750ms
        offset = sampleNumber * config.sampleIntervalMs;
        break;
        
      case 'descending':
        // Turbine: sample 9 = +0ms, sample 8 = +1000ms, sample 0 = +9000ms
        const maxSample = config.samplesPerPeriod - 1;
        offset = (maxSample - sampleNumber) * config.sampleIntervalMs;
        break;
        
      default:
        console.warn(`Unknown sample direction: ${config.sampleDirection}`);
        return null;
    }
    
    return offset;
  }
  
  /**
   * Validate that a sample number is within the expected range for the file type
   * @param sampleNumber The sample number to validate
   * @param config DTech file configuration
   * @returns True if sample number is valid, false otherwise
   */
  private static validateSampleNumber(sampleNumber: number, config: DTechFileConfig): boolean {
    if (!config.samplesPerPeriod) {
      return false;
    }
    
    // All sample numbers should be within 0 to (samplesPerPeriod - 1)
    // regardless of direction (ascending 0-39 or descending 9-0)
    const minSample = 0;
    const maxSample = config.samplesPerPeriod - 1;
    
    return sampleNumber >= minSample && sampleNumber <= maxSample;
  }
  
  /**
   * Extract sample numbers from data rows
   * @param data Array of data rows
   * @param sampleColumnName Name of the sample number column
   * @returns Object with sample numbers and valid indices
   */
  static extractSampleNumbers(
    data: Record<string, string>[],
    sampleColumnName: string
  ): {
    sampleNumbers: number[];
    validIndices: number[];
    extractionErrors: string[];
  } {
    const sampleNumbers: number[] = [];
    const validIndices: number[] = [];
    const extractionErrors: string[] = [];
    
    for (let i = 0; i < data.length; i++) {
      const sampleStr = data[i][sampleColumnName];
      
      if (!sampleStr || !sampleStr.trim()) {
        extractionErrors.push(`Missing sample number at row ${i + 1}`);
        continue;
      }
      
      const sampleNumber = parseInt(sampleStr.trim(), 10);
      
      if (isNaN(sampleNumber)) {
        extractionErrors.push(`Invalid sample number at row ${i + 1}: "${sampleStr}"`);
        continue;
      }
      
      sampleNumbers.push(sampleNumber);
      validIndices.push(i);
    }
    
    return {
      sampleNumbers,
      validIndices,
      extractionErrors
    };
  }
  
  /**
   * Find the sample number column in the data
   * @param sampleRow Sample data row to search in
   * @returns Sample column name or null if not found
   */
  static findSampleColumn(sampleRow: Record<string, string>): string | null {
    const availableColumns = Object.keys(sampleRow);
    
    // Common sample column names in D-Tech files
    const sampleColumnVariants = [
      'Sample no.',
      'Sample No.',
      'Sample no',
      'Sample No',
      'sample no.',
      'sample no',
      'SampleNo'
    ];
    
    for (const variant of sampleColumnVariants) {
      if (availableColumns.includes(variant)) {
        return variant;
      }
      
      // Try case-insensitive match
      const match = availableColumns.find(col => 
        col.toLowerCase().trim() === variant.toLowerCase().trim()
      );
      if (match) {
        return match;
      }
    }
    
    return null;
  }
}